import './belege.css';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { initialOf, t } from '../../i18n';
import { fmtDayMonth, monthName } from '../../lib/format';
import { useClientApp } from '../context';
import { NoteRow } from './Bell';
import { belegCount, belegePath, monthList } from './labels';
import { useNotifications, useShared } from './shared';
import type { HomeResponse, MissingResponse, Period } from './types';

// Teile der Übersicht, die zu Belegen gehören. Hinweise („Beleg fehlt“, Kommentare) gelten für alle Firmen
// des Logins (bei mehreren mit Firmennamen), der Belege-Aufruf für die oben gewählte Firma.

const useHome = (clientId: string) =>
  useShared<HomeResponse>(`/api/app/belege/${clientId}/home`, ['belege', 'fristen', 'clients']);

// „Ihre Kanzlei braucht noch: …“ (orange), bleibt stehen, bis der Beleg nachgereicht ist
export function BelegeMissingNotice() {
  const navigate = useNavigate();
  const { clients } = useClientApp();
  const res = useShared<MissingResponse>('/api/app/belege/missing', ['belege']);
  const notes = useNotifications();
  const missing = res.data?.missing ?? [];
  if (!missing.length) return null;

  const go = (clientId: string, year: number, month: number) => {
    // die passende Glocken-Meldung gilt damit als gesehen
    const open = (notes.data?.items ?? []).filter(
      (n) => n.type === 'beleg_fehlt' && !n.read && n.clientId === clientId && n.link?.year === year && n.link.month === month,
    );
    if (open.length) {
      void Promise.all(open.map((n) => api.post(`/api/app/notifications/${n.id}/read`).catch(() => {}))).then(() =>
        notes.reload(),
      );
    }
    navigate(belegePath(clientId, year, month));
  };

  return (
    <div className="notice warnbox" role="status">
      {missing.map((m) => (
        <div className="nt-r" key={`${m.clientId}-${m.year}-${m.month}`}>
          <span className="nt-i" aria-hidden="true">
            📎
          </span>
          <div className="tx">
            <b>{m.note ? t('Ihre Kanzlei braucht noch: {note}', { note: m.note }) : t('Ihre Kanzlei braucht noch: Belege')}</b>
            <small>
              {monthName(m.month)} {m.year}
              {clients.length > 1 ? ` · ${m.clientName}` : ''}
            </small>
          </div>
          <button type="button" className="btn2 dark" onClick={() => go(m.clientId, m.year, m.month)}>
            {t('Jetzt hochladen')}
          </button>
        </div>
      ))}
    </div>
  );
}

// Neue Kommentare der Kanzlei zu Belegen, bis sie geöffnet sind (bei mehreren Firmen mit Firmennamen)
export function BelegeCommentNotices() {
  const notes = useNotifications();
  const items = (notes.data?.items ?? []).filter((n) => n.type === 'beleg_comment' && !n.read);
  if (!items.length) return null;
  return (
    <div className="notice" role="status">
      {items.map((n) => (
        <NoteRow key={n.id} item={n} />
      ))}
    </div>
  );
}

// Großer Knopf „Rechnung hochladen“ für den Fokus-Monat (Sam 11.10.: mit Hinweis, was hier hochkommt)
export function BelegeCta({ clientId }: { clientId: string }) {
  const home = useHome(clientId);
  const f = home.data?.focus;
  if (!f) return null;
  const mn = monthName(f.month);
  const state =
    f.status === 'fehlt'
      ? f.note
        ? t('Ihre Kanzlei braucht noch: {note}', { note: f.note })
        : t('Ihre Kanzlei braucht noch: Belege')
      : f.status === 'gebucht'
        ? t('{month} hat Ihre Kanzlei schon gebucht', { month: mn })
        : f.count
          ? t('{count} für {month} hochgeladen', { count: belegCount(f.count), month: mn })
          : t('Noch keine Belege für {month}', { month: mn });
  return (
    <Link className="cta" to={belegePath(clientId, f.year, f.month)}>
      <span>
        <b>{t('Rechnung hochladen')}</b>
        <small>
          {t('Kontoauszüge, Eingangs- und Ausgangsbelege für {month} können Sie hier hochladen.', { month: mn })}
          <br />
          {state}
          {f.others.length > 0 && ` · ${t('auch noch offen: {months}', { months: monthList(f.others) })}`}
          {f.period && (
            <>
              <br />
              {deadlineLine(f.period)}
            </>
          )}
        </small>
      </span>
      <span className="chev" aria-hidden="true">
        ›
      </span>
    </Link>
  );
}

// „Für die Umsatzsteuer Juli–September bitte bis 09.10. – Abgabe ans Finanzamt bis 12.10.“
// (übermitteln tut der Steuerberater, nicht die Kanzlei)
function deadlineLine(p: Period): string {
  const range = p.fromMonth === p.toMonth ? monthName(p.toMonth) : `${monthName(p.fromMonth)}–${monthName(p.toMonth)}`;
  const deadline = fmtDayMonth(p.deadline);
  return p.asap
    ? t('Für die Umsatzsteuer {range} bitte so bald wie möglich – Abgabe ans Finanzamt bis {deadline}', { range, deadline })
    : t('Für die Umsatzsteuer {range} bitte bis {date} – Abgabe ans Finanzamt bis {deadline}', {
        range,
        date: fmtDayMonth(p.belegeBy),
        deadline,
      });
}

// Eintrag in der Liste der Bereiche
export function BelegeListItem({ clientId }: { clientId: string }) {
  return (
    <Link className="item" to={belegePath(clientId)}>
      <span className="ico" aria-hidden="true">
        {initialOf('Finanzbuchhaltung')}
      </span>
      <span className="tx">
        <b>{t('Finanzbuchhaltung')}</b>
        <small>{t('Belege, Rechnungen, Kontoauszüge')}</small>
      </span>
      <span className="chev" aria-hidden="true">
        ›
      </span>
    </Link>
  );
}
